import React from "react";
import { brand } from "@/lib/branding";

/**
 * Renders the current deployment's client-attribution mark (the "Serving X"
 * badge). Content comes from `lib/branding.js`, driven by the REACT_APP_BRAND
 * env var. Returns null if the active brand has no clientBrand block.
 *
 * Variants:
 *  - `variant="stacked"`  — logo + 3-line label (login pane)
 *  - `variant="compact"`  — small logo + 2-line label (sidebar footer)
 */
export default function ClientBrand({ variant = "compact", className = "" }) {
  const cb = brand.clientBrand;
  if (!cb) return null;

  const alt = cb.location ? `${cb.name} — ${cb.location}` : cb.name;

  if (variant === "stacked") {
    return (
      <div className={`flex items-center gap-3 ${className}`} data-testid="client-brand-stacked">
        <img src={cb.logo} alt={alt} className="h-10 w-auto" />
        <div className="border-l border-gray-300 pl-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-mono">{cb.relationLabel || "Serving"}</div>
          <div className="text-sm font-display font-bold tracking-tight text-gray-900 leading-tight">
            {cb.name}
          </div>
          {cb.location && (
            <div className="text-[11px] text-gray-500 font-mono">{cb.location}</div>
          )}
        </div>
      </div>
    );
  }

  // compact
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="client-brand-compact">
      <img src={cb.logo} alt={alt} className="h-6 w-auto flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold text-gray-800 tracking-tight leading-tight truncate">
          {cb.name}
        </div>
        {cb.location && (
          <div className="text-[9px] text-gray-500 font-mono leading-tight">{cb.location}</div>
        )}
      </div>
    </div>
  );
}
